import axios from "axios";
import { useUserStore } from "../store/useUserStore";
import { UserAdministrationInfo } from "../common/interfaces/common.interface";

let getToken: (() => Promise<string | undefined>) | null = null;

export const setTokenGetter = (fn: () => Promise<string | undefined>) => {
  getToken = fn;
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_SERVER_URL,
  timeout: 100000,
});

api.interceptors.request.use(
  async (config) => {
    if (getToken) {
      const token = await getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const ADMINISTRATION_INFO_PATH = '/user/administration-info';

/** Whether this account may use the admin panel at all - the rule ProtectedRoute applies. */
export const hasPanelAccess = (info?: UserAdministrationInfo | null): boolean =>
  !!(info?.isAdmin || info?.isKindergartenAdmin || info?.isSuperAdmin);

let accessCheck: Promise<void> | null = null;

/**
 * Asks the api who this user is now, once however many 403s arrive together, and keeps
 * the session only if they may still use the panel.
 */
const recheckAccess = (): Promise<void> => {
  if (!accessCheck) {
    accessCheck = api
      .get(ADMINISTRATION_INFO_PATH)
      .then((response) => {
        const info: UserAdministrationInfo | undefined = response.data?.results;
        if (hasPanelAccess(info)) useUserStore.getState().setUserInfo(info as UserAdministrationInfo);
        else useUserStore.getState().clear();
      })
      // a 401 or 403 here has already cleared the session (below); a network error says
      // nothing about access, so the session stays
      .catch(() => undefined)
      .finally(() => {
        accessCheck = null;
      });
  }
  return accessCheck;
};

// The admin may have been deactivated on the api while the persisted userInfo still says
// they are one, which left pages stuck on a loader and firing errors; dropping the session
// makes ProtectedRoute send them to the unauthorized page. A 401 always means that. A 403
// usually means only "not this": another kindergarten's invoice, a feature switched off.
// Logging the admin out for that threw away their session over one link, so a 403 now
// asks who the user is, and only an answer that they have lost access ends the session.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (status === 401 || (status === 403 && error.config?.url === ADMINISTRATION_INFO_PATH)) {
      useUserStore.getState().clear();
    } else if (status === 403) {
      void recheckAccess();
    }

    return Promise.reject(error);
  }
);

export default api;
